/**
 * Voice dictation control for the Indy composer.
 *
 * Icon-only at a fixed size in every state, so the composer's action row never
 * reflows when dictation starts. The microphone glyph stays put even while
 * listening — the shared AudioLevelIndicator carries a 40px min-width that would
 * widen the button well past an icon — and liveness is carried by colour plus a
 * pulse driven by real microphone amplitude. The composer's placeholder says
 * what to do to finish (see ChatComposer), which keeps the instruction visible
 * without widening the control.
 *
 * Dictation writes into the composer's textarea through the shared
 * `useDictationManager`, which targets the focused text input. The button takes
 * focus on click, so the textarea is focused first — otherwise the manager would
 * resolve the button as its target and drop the transcript.
 *
 * Holding Control while the composer is focused is the push-to-talk path to the
 * same session (see `usePushToTalk`); the click path toggles and stays on.
 * Double-tapping Control is a keyboard alias for the click (see
 * `useDoubleTapKey`) — disjoint from the hold, which needs one press past
 * 400ms where a tap is released under 250ms.
 */
import { Button, Icon, Tooltip, toast } from "@indigo-components";
import { FC, useCallback, useEffect, useRef } from "react";
import {
  useDictationAudioLevel,
  useDictationManager,
} from "~/hooks/useDictationManager";
import { useDoubleTapKey } from "~/hooks/useDoubleTapKey";
import { useDictationWarmup } from "~/hooks/usePreconnectDictation";
import {
  type PushToTalkStopReason,
  usePushToTalk,
} from "~/hooks/usePushToTalk";
import { isIndyComposerFocused } from "~/pages/indy/indyComposerTarget";
import "./IndyDictationButton.css";

/** Distinguishes this button from Assessment's, which shares the manager. */
export const INDY_DICTATION_SECTION_ID = "indy-composer";

/** Shown when a hold ends before the microphone ever opened. */
export const PUSH_TO_TALK_NOT_READY_MESSAGE =
  "Microphone was still connecting — hold Control again to dictate.";

interface IndyDictationButtonProps {
  /** The composer textarea that transcribed text should land in. */
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
  /** Streaming or uploading — dictating into a locked composer would be lost. */
  disabled?: boolean;
}

export const IndyDictationButton: FC<IndyDictationButtonProps> = ({
  textareaRef,
  disabled = false,
}) => {
  const dictation = useDictationManager();
  const { audioLevel } = useDictationAudioLevel();
  // Name the surface rather than relying on the intent atom: this composer
  // always dictates into Indy, and the atom is claimed by a parent effect that
  // can run after the composer's own focus handler.
  const warmDictation = useDictationWarmup("indy");

  const isThisButton = dictation.activeSection === INDY_DICTATION_SECTION_ID;
  const isListening = dictation.isActive && isThisButton;
  const isConnecting = dictation.isConnecting && isThisButton;

  // If the whole composer unmounts while this button owns the session —
  // panel closed, full-screen exited — nothing else will stop the microphone,
  // and the connection's idle timeout never fires while recording. Refs so
  // the unmount cleanup sees current values without re-running per render.
  const ownsDictationRef = useRef(false);
  ownsDictationRef.current = isListening || isConnecting;
  const stopRef = useRef(dictation.stopDictation);
  stopRef.current = dictation.stopDictation;
  useEffect(
    () => () => {
      if (ownsDictationRef.current) {
        stopRef.current();
      }
    },
    [],
  );

  // ── Push-to-talk: hold Control while the composer is focused ──
  // startDictation resolves only once the connection is up, so a release
  // during that window has nothing to stop yet. Defer it rather than dropping
  // it, or a quick tap-and-release leaves the microphone open.
  //
  // Tokens rather than a boolean: hold → release → re-hold can put two starts
  // in flight, and with a single flag the first one's settlement clears the
  // "starting" state while the second is still connecting. The next release
  // would then stop nothing (stopRecording no-ops before isRecording is set),
  // and the second start would go on to open the microphone with the UI
  // showing "inactive" and no stop control.
  const startTokenRef = useRef(0);
  const settledTokenRef = useRef(0);
  const stopRequestedRef = useRef(false);

  const handlePushToTalkStart = useCallback(() => {
    const token = ++startTokenRef.current;
    stopRequestedRef.current = false;
    // The manager targets whichever text input holds focus; the gesture is
    // already gated on the composer having it, so no focus call is needed.
    void dictation
      .startDictation(INDY_DICTATION_SECTION_ID, "indy")
      .finally(() => {
        // A newer start superseded this one — it owns the session now, and
        // clearing the in-flight state here would strand its release.
        if (token !== startTokenRef.current) return;
        settledTokenRef.current = token;
        if (stopRequestedRef.current) {
          stopRequestedRef.current = false;
          dictation.stopDictation();
        }
      });
  }, [dictation]);

  const handlePushToTalkStop = useCallback(
    (reason: PushToTalkStopReason) => {
      if (startTokenRef.current !== settledTokenRef.current) {
        stopRequestedRef.current = true;
        // The start has not settled, and startRecording() flips isRecording on
        // its very last line — so the microphone never opened and everything
        // said during the hold is gone. A cold connect runs 2.5-4s against a
        // 400ms threshold, so this is the whole "the first hold does nothing"
        // report. Say so: silence reads as the feature being broken. The
        // connection this attempt paid for stays open, so the retry is instant.
        //
        // Only on a deliberate release. The gesture also stands down on a
        // pointer press (Control+click is the macOS context menu), a chord,
        // focus leaving, an app switch and unmount — telling someone who never
        // meant to dictate to "hold Control again" is noise, and on unmount the
        // composer it refers to is already gone.
        if (reason === "release") {
          toast({ message: PUSH_TO_TALK_NOT_READY_MESSAGE, duration: 3000 });
        }
        return;
      }
      dictation.stopDictation();
    },
    [dictation],
  );

  const isTargetFocused = useCallback(
    () => document.activeElement === textareaRef.current,
    [textareaRef],
  );

  usePushToTalk({
    // A session already running (click-started, or another surface's) owns the
    // microphone; the hold must not open a second one. `isConnecting` closes
    // the same door during the 2.5-4s cold connect, when `isActive` is still
    // false — otherwise a hold started in that window opens a second session
    // behind the first. Warming does not set this status (it touches only the
    // WebRTC singleton), so a hold's own `onHoldStart` cannot gate it off.
    enabled:
      dictation.isSupported &&
      !disabled &&
      !dictation.isActive &&
      !dictation.isConnecting,
    isTargetFocused,
    onStart: handlePushToTalkStart,
    onStop: handlePushToTalkStop,
    // Spend the hold threshold warming the connection instead of making the
    // user wait through it after they have already started speaking.
    onHoldStart: warmDictation,
  });

  const handleToggle = useCallback(() => {
    if (!dictation.isActive) {
      // Append to whatever is already typed rather than overwriting it.
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    }
    void dictation.toggleDictation(INDY_DICTATION_SECTION_ID, "indy");
  }, [dictation, textareaRef]);

  // ── Double-tap Control: a keyboard alias for the button above ──
  // Deliberately routed through `handleToggle` rather than the push-to-talk
  // path: this gesture is the *click* expressed on the keyboard, so it focuses
  // the textarea, appends rather than overwrites, and toggles a session that
  // stays on. None of the in-flight token bookkeeping applies — that exists
  // because a hold can end mid-connect, and a double-tap has already ended.
  //
  // The two gestures share Control without colliding because they are disjoint
  // per press: a tap is released under 250ms, a hold opens the mic at 400ms.
  // A press that splits the difference voids the double-tap and does not reach
  // the hold threshold, and tap-tap-then-hold degrades cleanly to push-to-talk.
  //
  // Scoped to composer focus, like the hold — Control pressed while reading the
  // conversation must not open the microphone. The check is the whole composer
  // rather than the textarea alone (which is what push-to-talk requires, since
  // it dictates into whatever holds focus): `handleToggle` focuses the textarea
  // itself, so the gesture still works with the attach or mic button focused.
  // This is also the exact complement of Assessment's gesture, which stands
  // down on `[data-indy-composer]` — so at most one of them ever fires.
  useDoubleTapKey({
    // No isConnecting gate: the manager's toggleDictation refuses to start a
    // second session while one is still coming up.
    enabled: dictation.isSupported && !disabled,
    canTrigger: isIndyComposerFocused,
    onTrigger: handleToggle,
  });

  if (!dictation.isSupported) {
    return null;
  }

  const label = isConnecting
    ? "Connecting to microphone"
    : isListening
      ? "Finish dictation"
      : "Dictate a message";
  // Both gestures are invisible otherwise — the tooltip is the only place a
  // physician discovers them. Kept out of the aria-label, which should name the
  // control rather than enumerate its shortcuts.
  const tooltipTitle =
    isConnecting || isListening
      ? label
      : `${label} (hold or double-tap Control)`;

  const isDisabled = disabled || isConnecting;

  const button = (
    <Button
      // Ghost at rest, not secondary: inside the composer surface an outlined
      // box would be a third material next to the plus glyph and the send
      // circle. Danger while listening still needs the fill — that one is
      // state, not decoration.
      variant={isListening ? "danger" : "ghost"}
      size="md"
      type="button"
      // Round, matching the plus and send buttons it sits between — a rounded
      // square there was the odd shape out and made the glyph read as larger
      // than it is.
      className="w-8 shrink-0 rounded-indigo-full p-0"
      aria-label={label}
      aria-pressed={isListening}
      disabled={isDisabled}
      onClick={handleToggle}
    >
      {isConnecting ? (
        <Icon name="spinner" />
      ) : (
        <span
          className="indy-dictation-pulse"
          // Tracks live microphone amplitude, so it cannot live in CSS.
          // Clamped so a loud room nudges the glyph rather than jumping it.
          style={
            isListening
              ? {
                  transform: `scale(${1 + Math.min(audioLevel * 1.2, 0.25)})`,
                }
              : undefined
          }
        >
          <Icon name="microphone" />
        </span>
      )}
    </Button>
  );

  return (
    // Placement is explicit: the design system defaults to bottom-end, and this
    // button sits on the bottom edge of the composer where that would render
    // off-screen.
    //
    // The wrapper is unconditional. A disabled button emits no hover events, so
    // the tooltip needs a live element to attach to — but making the wrapper
    // conditional changes the element tree when `isDisabled` flips, remounting
    // the Button on every connecting transition and dropping keyboard focus
    // mid-click. inline-flex keeps it layout-neutral in the flex action row.
    <Tooltip content={tooltipTitle} placement="top">
      <span className="inline-flex">{button}</span>
    </Tooltip>
  );
};
